import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { IconPlay, IconClose, IconDownload } from '../lib/icons.jsx';
import { useGallery } from './GalleryLayout.jsx';
import GalleryBar from './GalleryBar.jsx';
import { filmName, formatDuration } from './DownloadUI.jsx';

/**
 * The Film page, styled after a streaming service's landing screen: a
 * full-screen hero with the featured film playing muted behind the couple's
 * name, and the films as a horizontal "reel" of cards floating over the bottom
 * of the hero. Picking a card opens the film in a focused player.
 *
 * ⚠️ Films play from Bunny Stream's adaptive embed — the hero background and the
 * player are both Bunny iframes. The background is muted/looping and inert; the
 * player has sound and controls. Nothing here re-encodes or self-hosts video.
 */
export default function FilmPage() {
  const { slug, gallery, photos, videos, downloadsOn, openDownload, downloadOne } = useGallery();
  const [playing, setPlaying] = useState(null); // film open in the player overlay
  const [videoBg, setVideoBg] = useState(false);

  if (videos.length === 0) return <Navigate to={`/${slug}/photos`} replace />;

  const featured = videos[0];

  // Muted background film on desktop only (spares mobile data); the poster
  // covers the moment before it starts, and stands in entirely on phones.
  useEffect(() => {
    const wide = window.matchMedia('(min-width: 768px)').matches;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData = navigator.connection?.saveData;
    setVideoBg(wide && !reduced && !saveData && !!featured?.embed);
  }, [featured]);

  return (
    <div className="gal film-page">
      <GalleryBar slug={slug} videos={videos} photos={photos} downloadsOn={downloadsOn} onDownload={openDownload} transparent />

      <section className="film-stage">
        <div className="hero-media">
          {featured.poster && <img className="hero-img loaded" src={featured.poster} alt="" />}
          {videoBg && (
            <div className="hero-video" aria-hidden>
              <iframe
                src={`${featured.embed}&autoplay=true&loop=true&muted=true&preload=true`}
                title=""
                tabIndex={-1}
                allow="autoplay; encrypted-media; picture-in-picture;"
              />
            </div>
          )}
        </div>

        <div className="film-stage-inner">
          <span className="label on-dark">{videos.length > 1 ? 'The Films' : 'The Film'}</span>
          <h1 className="hero-name">{gallery.client_name}</h1>
          <button className="primary film-hero-play" onClick={() => setPlaying(featured)}>
            <IconPlay width={17} height={17} style={{ marginLeft: 2 }} />
            Play {videos.length > 1 ? 'highlights' : 'the film'}
          </button>
        </div>

        {/* The reel — a horizontal row of films floating over the hero. */}
        <div className="film-reel">
          <div className="reel-track">
            {videos.map((v, i) => (
              <button key={v.id} className="reel-card" onClick={() => setPlaying(v)}>
                <span className="reel-thumb">
                  {v.poster ? <img src={v.poster} alt="" loading="lazy" /> : <span className="reel-blank" />}
                  <span className="reel-play"><IconPlay width={22} height={22} style={{ marginLeft: 2 }} /></span>
                  {v.duration_secs ? <span className="reel-dur">{formatDuration(v.duration_secs)}</span> : null}
                </span>
                <span className="reel-title">{filmName(v, i)}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {playing && (
        <FilmPlayer
          film={playing}
          allowDownload={gallery.allow_video_download}
          onDownload={downloadOne}
          onClose={() => setPlaying(null)}
        />
      )}
    </div>
  );
}

/** Focused, full-screen player for one film. */
function FilmPlayer({ film, allowDownload, onDownload, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div className="film-player-overlay" onClick={onClose}>
      <div className="film-player-inner" onClick={(e) => e.stopPropagation()}>
        <div className="film-player-bar">
          <span className="serif" style={{ fontSize: '1.25rem', color: '#fff' }}>{film.title || film.filename}</span>
          <div className="row" style={{ gap: '0.5rem' }}>
            {allowDownload && (
              <button className="sm" onClick={() => onDownload(film)} style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.25)', color: '#fff' }}>
                <IconDownload width={15} height={15} />
                <span className="hide-sm">Download</span>
              </button>
            )}
            <button className="icon-only" onClick={onClose} aria-label="Close" style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.25)', color: '#fff' }}>
              <IconClose />
            </button>
          </div>
        </div>
        <div className="film-frame">
          {film.embed && (
            <iframe
              className="film-embed"
              src={`${film.embed}&autoplay=true`}
              title={film.title || film.filename}
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
              allowFullScreen
            />
          )}
        </div>
      </div>
    </div>
  );
}
